<!-- 模型卡片组件 -->
<template>
  <el-card class="model-card" :id="model.id">
    <div class="model-header">
      <h2 class="model-title">{{ model.name }}</h2>
    </div>

    <div class="model-content">
      <div class="model-image-section">
        <el-carousel trigger="click" height="350px" indicator-position="bottom">
          <el-carousel-item v-for="(item, index) in model.carouselItems" :key="index">
            <img :src="item.image" :alt="item.title" class="carousel-img">
          </el-carousel-item>
        </el-carousel>
        <div class="download-section">
          <el-button type="primary" size="small" class="download-button" @click="openDownloadModal">
            <i class="el-icon-download"></i> {{ model.downloadText }}
          </el-button>
        </div>
      </div>

      <div class="model-info">
        <h3 class="info-title">基本信息</h3>
        <ul class="info-list">
          <li v-for="(item, index) in model.infoItems" :key="index" class="info-item">
            <span class="info-label">{{ item.label }}</span>
            <span class="info-value">{{ item.value }}</span>
          </li>
        </ul>
      </div>
    </div>

    <el-dialog title="选择要下载的图片" :visible.sync="downloadModalVisible" :width="dialogWidth" :top="dialogTop"
      :modal-append-to-body="false" :append-to-body="true" :before-close="handleModalClose" >
      <div class="download-modal-content">
        <div class="image-selection-list">
          <div class="image-item" v-for="(item, index) in model.carouselItems" :key="index"
            :class="{ selected: selectedImages.includes(index) }" @click="toggleImageSelection(index)">
            <div class="thumbnail-wrapper">
              <img :src="getImageUrl(item.image)" :alt="item.title" class="thumbnail-img" loading="lazy">
              <div class="selection-mark" v-if="selectedImages.includes(index)">
                <i class="el-icon-check"></i>
              </div>
            </div>
            <div class="image-title">{{ item.title }}</div>
          </div>
        </div>

        <div class="selection-actions" v-if="model.carouselItems.length > 1">
          <el-button type="text" @click="selectAllImages" size="small">全选</el-button>
          <el-button type="text" @click="deselectAllImages" size="small">取消全选</el-button>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="downloadModalVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmDownload" :disabled="selectedImages.length === 0">
          确认下载 ({{ selectedImages.length }})
        </el-button>
      </div>
    </el-dialog>
  </el-card>
</template>

<script>
export default {
  name: 'ModelCard',
  props: {
    model: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      downloadModalVisible: false,
      selectedImages: [],
      dialogWidth: '70%',
      dialogTop: '50px'
    };
  },
  created() {
    this.calcDialogSize();
    window.addEventListener('resize', this.calcDialogSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calcDialogSize);
  },
  methods: {
    calcDialogSize() {
      const windowWidth = window.innerWidth;
      if (windowWidth <= 768) {
        this.dialogWidth = '90%';
        this.dialogTop = '20px';
      } else if (windowWidth <= 1200) {
        this.dialogWidth = '70%';
        this.dialogTop = '30px';
      } else {
        this.dialogWidth = '800px';
        this.dialogTop = '50px';
      }
    },
    getImageUrl(imageSrc) {
      if (typeof imageSrc === 'number') {
        const img = new Image();
        img.src = imageSrc;
        return img.src;
      }
      return imageSrc;
    },
    openDownloadModal() {
      this.calcDialogSize();
      this.selectedImages = [0];
      this.downloadModalVisible = true;
    },
    handleModalClose() {
      this.selectedImages = [];
      this.downloadModalVisible = false;
    },
    toggleImageSelection(index) {
      if (this.selectedImages.includes(index)) {
        this.selectedImages = this.selectedImages.filter(idx => idx !== index);
      } else {
        this.selectedImages.push(index);
      }
    },
    selectAllImages() {
      this.selectedImages = Array.from(
        { length: this.model.carouselItems.length },
        (_, i) => i
      );
    },
    deselectAllImages() {
      this.selectedImages = [];
    },
    async confirmDownload() {
      if (this.selectedImages.length === 0) {
        this.$message.warning('请至少选择一张图片进行下载');
        return;
      }

      try {
        for (const index of this.selectedImages) {
          const item = this.model.carouselItems[index];
          await this.downloadSingleImage(item.image, `${this.model.name}-${item.title}`);
        }

        this.downloadModalVisible = false;
        this.selectedImages = [];
      } catch (error) {
        console.error('下载失败:', error);
        this.$message.error('图片下载失败，请重试');
      }
    },
    async downloadSingleImage(imageSrc, filename) {
      try {
        let imageUrl = this.getImageUrl(imageSrc);

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.crossOrigin = 'Anonymous';

        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error(`图片 "${filename}" 加载失败`));
          img.src = imageUrl;
          if (img.complete) {
            resolve();
          }
        });

        canvas.width = img.width;
        canvas.height = img.height;
        ctx.drawImage(img, 0, 0);

        await new Promise((resolve) => {
          canvas.toBlob((blob) => {
            if (blob) {
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `${filename}.jpg`;
              document.body.appendChild(link);
              link.click();

              setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                resolve();
              }, 100);
            } else {
              resolve();
            }
          }, 'image/jpeg');
        });
      } catch (error) {
        throw new Error(`下载 "${filename}" 失败: ${error.message}`);
      }
    }
  }
};
</script>

<style scoped>
.model-card {
  margin-bottom: 40px;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e4e7ed;
  width: 100%;
  min-width: 1100px;
  box-sizing: border-box;
}

.model-header {
  padding: 15px 20px;
  background: #fafafa;
  border-bottom: 1px solid #e4e7ed;
}

.model-title {
  font-size: 20px;
  color: #333;
  margin: 0;
  font-weight: bold;
}

.model-content {
  padding: 20px;
  display: flex;
  gap: 30px;
  align-items: flex-start;
  width: 100%;
  box-sizing: border-box;
}

.model-image-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  min-width: 500px;
}

::v-deep .el-carousel__container {
  height: 350px !important;
}

.carousel-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #ffffff;
}

.download-section {
  margin-top: 10px;
  text-align: center;
}

.download-button {
  background-color: #409eff;
  border-color: #409eff;
}

.download-button:hover {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

.model-info {
  width: 320px;
  flex-shrink: 0;
  background-color: #fafafa;
  padding: 20px 25px;
  border-radius: 8px;
}

.info-title {
  font-size: 18px;
  color: #333;
  margin-bottom: 15px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e4e7ed;
}

.info-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.info-item {
  margin-bottom: 12px;
  line-height: 1.6;
  display: flex;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: bold;
  color: #666;
  margin-right: 10px;
  min-width: 60px;
}

.info-value {
  color: #333;
  flex: 1;
}

::v-deep .el-dialog {
  max-width: 800px !important;
  max-height: 90vh !important;
  overflow: hidden;
}

::v-deep .el-dialog__body {
  padding: 10px 20px !important;
  max-height: calc(90vh - 120px) !important;
  overflow: hidden;
}

.download-modal-content {
  padding: 5px 0;
  height: 100%;
}

.image-selection-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 15px;
  max-height: calc(90vh - 180px);
  overflow-y: auto;
  padding: 10px;
  margin-bottom: 15px;
  scrollbar-width: thin;
}

.image-selection-list::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.image-selection-list::-webkit-scrollbar-thumb {
  background-color: #409eff;
  border-radius: 3px;
}

.image-selection-list::-webkit-scrollbar-track {
  background-color: #f0f0f0;
  border-radius: 3px;
}

.image-item {
  width: auto !important;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.image-item.selected {
  border-color: #409eff;
  background-color: #f0f7ff;
}

.image-item:hover {
  border-color: #99d1ff;
}

.thumbnail-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: #f8f9fa;
}

.selection-mark {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 20px;
  height: 20px;
  background-color: #409eff;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.image-title {
  font-size: 12px;
  color: #666;
  text-align: center;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  padding: 0 4px;
}

.selection-actions {
  text-align: right;
  margin-bottom: 5px;
  padding-right: 5px;
}

.selection-actions button {
  font-size: 12px;
  padding: 0 8px;
}

::v-deep .el-dialog__footer {
  padding: 10px 20px !important;
  border-top: 1px solid #e4e7ed;
}

.dialog-footer button {
  padding: 8px 16px;
  font-size: 14px;
}
</style>